import { Component, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MenuController, Platform } from '@ionic/angular';
import { TranslateConfigService } from './services/translate-config.service';
import { SignalRService } from './services/signalR.service';
import { AuthService } from './services/auth.service';
// import { BackgroundRunner } from '@capacitor/background-runner';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { EventKnightService } from './services/eventKnight.service';
import { AppState, App } from '@capacitor/app';
import { LocationBackgroundService } from './services/locationBackground.service';
import { StorageService } from './services/storage.service';
import { initializeApp } from 'firebase/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import { Device } from '@capacitor/device';
import { FirebaseService } from './services/firebase.service';
import { UserStatusServiceFactory } from './services/user-status.service';
import { BgServiceSettings } from './shared/models/event.model';
import { getDatabase, onValue, ref } from 'firebase/database';
import { KioskMode } from './plugins/kiosk-mode';
import { NetworkService } from './services/network.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  isNativeApp = false;
  user: any;
  constructor(
    private menuCtrl: MenuController,
    private router: Router,
    private translationConfig: TranslateConfigService,
    private eventKnightService: EventKnightService,
    private signalRService: SignalRService,
    private userStatusService: UserStatusServiceFactory,
    private storageService: StorageService,
    private locationService: LocationBackgroundService,
    private platform: Platform,
    private authService: AuthService,
    private firebaseService: FirebaseService,
    private zone: NgZone,
    public networkService: NetworkService
  ) {
    this.firebaseService.getApp();
    translationConfig.setLanguage('en');
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Automatically close any open menu on navigation
        this.menuCtrl.close();
      }
    });
    
  }

  async ngOnInit() {

    this.isNativeApp = Capacitor.isNativePlatform();
    const currentLoginUser = await this.authService.getCurrentUserValue();
    if (currentLoginUser) {
      this.user = currentLoginUser;
      // Set user_id into background service
      if (this.isNativeApp) {
        if(Capacitor.getPlatform() === 'android'){
          await this.locationService.enableForegroundService();
        }
        
        this.initializeAppStateListener();
      }

      await this.signalRService.startConnection(currentLoginUser.access_token!);
      this.signalRService.addListener();
    }

    const info = await Device.getInfo();
    if (Capacitor.getPlatform() === 'ios' || (Capacitor.getPlatform() === 'android' && Number(info.osVersion) >= 15)) {
      await this.handleEdgeToEdgePadding();
    }

    this.platform.ready().then(() => {
      // this.setupConsoleLogger();
      
    });
    
    const isAndroid = Capacitor.getPlatform() === 'android';
    if (this.isNativeApp && isAndroid) {
      const checkKioskMode = await KioskMode.hasKioskMode();
      if(checkKioskMode.support == true){
        this.listenForUpdates();
      }
    }

    
  }

  private setupConsoleLogger() {
    // hook only once
    (['log', 'info', 'warn', 'error'] as const).forEach((level) => {
      const original = console[level].bind(console);
      (console as any)[level] = (...args: any[]) => {
        // normal output
        original(...args);

        try {
          // send to Capacitor native plugin
          (window as any).Capacitor.Plugins.BgLocationPermission.echo({
            level,
            message: args.map(a => {
              try {
                return typeof a === 'string' ? a : JSON.stringify(a);
              } catch {
                return String(a);
              }
            }).join(' ')
          });
        } catch (e) {
          // plugin not available yet, ignore
        }
      };
    });
  }

  async handleEdgeToEdgePadding() {
    // Ensure the app works on new Android API >= 35
    await this.platform.ready();

    try {
      // Handle UI
      await EdgeToEdge.enable();
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: '#000000' });
      await EdgeToEdge.setBackgroundColor({ color: '#000000' });
      await StatusBar.setStyle({ style: Style.Dark }); // or Style.Light
      await StatusBar.show();
    } catch (error) {
      console.error('Status bar setup failed:', error);
    }
  }

  // initBackgroundGeolocation() {
  //   BackgroundGeolocation.ready({
  //     desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_HIGH,
  //     distanceFilter: 10,
  //     stopOnTerminate: false,
  //     startOnBoot: true,
  //     notification: {
  //       title: "Tracking",
  //       text: "Your location is being tracked in background"
  //     },
  //     locationAuthorizationRequest: 'Always',
  //     backgroundPermissionRationale: {
  //       title: "Allow {applicationName} to access this device's location even when closed or not in use.",
  //       message: "This app collects location data to enable background tracking.",
  //       positiveAction: 'Change to "Allow all the time"',
  //       negativeAction: 'Cancel'
  //     }
  //   }, (state: any) => {
  //     console.log("[ready] BackgroundGeolocation is configured and ready: ", state.enabled);

  //     if (!state.enabled) {
  //       BackgroundGeolocation.start();
  //     }
  //   });

  //   BackgroundGeolocation.onLocation((location: any) => {
  //     console.log('[location] -', location);
  //     // Send to server or store locally
  //   }, (error: any) => {
  //     console.error('[location] ERROR -', error);
  //   });

  //   BackgroundGeolocation.onMotionChange((event: any) => {
  //     console.log('[motionchange]', event.isMoving, event.location);
  //   });
  // }
  async ngAfterViewInit(): Promise<void> {
    // Check version and clear cache for web
    if (!Capacitor.isNativePlatform()) await this.checkAppVersion();
  }

  async checkAppVersion() {
    const currentVersion = environment.version;
    const storedVersion = await this.storageService.get('app_version');
    // console.log(storedVersion);
    if (storedVersion !== currentVersion) {
      // console.log(storedVersion, currentVersion);
      await this.storageService.set('app_version', currentVersion);

      // Clear all caches
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });

      // Unregister service workers
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (let registration of registrations) {
            registration.unregister();
          }
        });
      }

      // Reload page after clearing
      location.reload();
    }
  }

  initializeAppStateListener() {
    const isIos = Capacitor.getPlatform() === 'ios';
    console.log('isiOs', isIos);

    App.addListener('appStateChange', async (state: AppState) => {
      // const currentLoginUser = await this.authService.getCurrentUserValue();
      // const isStartedDutyToday = await this.storageService.get(
      //   'isStartedDutyToday'
      // );
      console.log('appStateChange', state.isActive);

      const bgServiceSettings: BgServiceSettings = await this.storageService.get('bgServiceSettings');
      console.log(bgServiceSettings);

      if (!bgServiceSettings || !bgServiceSettings.isStartedDuty || bgServiceSettings.isStartedBreak) {
        // if (!isIos) await this.locationService.disableForegroundService();
        await this.locationService.stopTracking();
        return;
      }
      

      if (!state.isActive) {
        // if(!isIos) await this.locationService.enableForegroundService();
        await this.locationService.startTrackingV3(bgServiceSettings);
      } else {
        // if(!isIos) await this.locationService.disableForegroundService();
        await this.locationService.stopTracking();

        // Sync data to server
        // if(currentLoginUser && currentLoginUser.user_id)
        //   await this.locationService.sendStoredLocationsToServer(currentLoginUser.user_id);
      }

      // if (!state.isActive) {
      //   await this.locationService.enableForegroundService();
      //   await this.locationService.startTracking();
      // } else {
      //   await this.locationService.disableForegroundService();
      //   await this.locationService.stopTracking();
      //   if(currentLoginUser && currentLoginUser.user_id)
      //     await this.locationService.sendStoredLocationsToServer(currentLoginUser.user_id);
      // }
    });


  }

  // async checkUserLocationStatus() {
  //   await this.eventKnightService.getLocationStatus().subscribe(async (res) => {
  //     if(res && res.isActive)
  //     {
  //       await this.locateUser();
  //     }
  //   });
  // }

  // async locateUser() {
  //   try {
  //     const position = await Geolocation.getCurrentPosition();

  //     const userLocation = {
  //       lat: position.coords.latitude,
  //       lng: position.coords.longitude,
  //     };

  //     // Send user location to the server
  //     const model = {
  //       latitude: userLocation.lat,
  //       longitude: userLocation.lng,
  //       isActive: true
  //     }
  //     this.eventKnightService.updateLocation(model).subscribe(async () => {

  //       // Update background job not send the data
  //       if (Capacitor.isNativePlatform()) {
  //         const result = await BackgroundRunner.dispatchEvent({
  //           label: 'com.urknight.app.check',
  //           event: 'enableLocationTrack', // 'enableLocationTrack',
  //           details: { },
  //         });
  //       }
  //     }, (err: any) => {
  //       //this.toastService.error(err);
  //     });

  //     return true;

  //   } catch (error) {
  //     //console.error('Error getting location:', error);
  //     //alert('Unable to fetch your location. Please enable location services.');

  //     return false;
  //   }
  // }

  // async saveUserToJob(userId: string) {
  //   if (!Capacitor.isNativePlatform()) {
  //     return;
  //   }

  //   const result = await BackgroundRunner.dispatchEvent({
  //     label: 'com.urknight.app.check',
  //     event: 'setUserId',
  //     details: { userId: userId, baseApiUrl: environment.apiEndpoint },
  //   });
  //   console.log('load result', result);
  // }

  async listenForUpdates() {
    const currentVersion = await KioskMode.getVersionCode();
    const versionCode = currentVersion.versionCode;
    const db = getDatabase();
    const appUpdateRef = ref(db, `AppVersion`);
    onValue(appUpdateRef, snapshot => {
          this.zone.run( async () => {
            const data = snapshot.val();
            if (data) {
              if(versionCode < data.versionCode){
                // console.log('Update available! APK URL:', data.url);
                await KioskMode.updateVersion({url: data.url});
              }
            } else {
            }
        });
    });
  }
}
